import { useState, useEffect, useRef } from "react";
import { Search, Menu, Plus, Bell, Pencil, Check, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { Icon } from "@/lib/icons";
import RolePreviewSwitcher, { PreviewBanner } from "@/components/RolePreviewSwitcher";
import { useContent } from "@/context/ContentContext";
import { triggerQuickAdd } from "@/lib/quickAdd";
import GlobalSearch from "@/components/GlobalSearch";
import ClaudeConnector from "@/components/ClaudeConnector";

// Everything a person could create from anywhere, mapped to the section that
// owns its create form. Each needs edit-level access to that section.
const QUICK_ADD = [
  { section: "membership_directory", label: "Member", icon: "Users" },
  { section: "visitors", label: "Visitor", icon: "UserPlus" },
  { section: "events", label: "Event", icon: "Calendar" },
  { section: "grants", label: "Grant", icon: "Landmark" },
  { section: "legacy_bricks", label: "Brick order", icon: "Blocks" },
  { section: "garden_plots", label: "Garden plot", icon: "LayoutGrid" },
  { section: "partnerships", label: "Partnership", icon: "Handshake" },
  { section: "membership_codes", label: "Membership code", icon: "TicketPercent" },
  { section: "targeted_messages", label: "Targeted message", icon: "MessageSquare" },
];
const RANK = { none: 0, view: 1, edit: 2, full: 3 };

export default function Header({ me, onMenu }) {
  const isSuper = me?.user?.is_super_admin;
  const { editMode, setEditMode, get } = useContent();
  const [connectorOpen, setConnectorOpen] = useState(false);
  const perms = me?.permissions || {};
  const allowed = QUICK_ADD.filter((o) => (RANK[perms[o.section]] || 0) >= RANK.edit);
  return (
    <>
      <header className="bg-cream border-b border-ink/15 px-4 lg:px-6 py-3 flex items-center gap-2 lg:gap-3 sticky top-0 z-20">
        {onMenu && <button
          className="min-[900px]:hidden p-2 -ml-2 text-ink"
          onClick={onMenu}
          aria-label="Open menu"
          data-testid="menu-button"
        >
          <Menu className="w-6 h-6" />
        </button>}

        {/* Global search */}
        <GlobalSearch />

        <div className="ml-auto flex items-center gap-2">
          {isSuper && (
            <button
              data-testid="edit-mode-toggle"
              onClick={() => setEditMode(!editMode)}
              className={`flex items-center gap-1.5 rounded-lg px-2.5 sm:px-3 py-2.5 min-h-[44px] text-[14px] font-semibold border ${editMode ? "bg-maroon text-cream border-maroon" : "border-ink/20 text-ink hover:bg-ink/5"}`}
              title="Turn on to click any wording and edit it in place"
            >
              {editMode ? <Check className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
              <span className="hidden sm:inline">{editMode ? "Done editing" : "Edit text"}</span>
            </button>
          )}
          {isSuper && <RolePreviewSwitcher />}
          {me?.is_super_admin && (
            <button
              data-testid="claude-connector-btn"
              onClick={() => setConnectorOpen(true)}
              className="flex items-center gap-1.5 rounded-lg px-2.5 sm:px-3 py-2.5 min-h-[44px] text-[14px] font-semibold border border-ink/20 text-ink hover:bg-ink/5"
              title="Connect this account to Claude"
            >
              <Icon name="Plug" className="w-4 h-4" /><span className="hidden sm:inline">Claude</span>
            </button>
          )}
          <NotificationsBell />
          <QuickAddMenu allowed={allowed} />
        </div>
      </header>
      <PreviewBanner />
      <ClaudeConnector open={connectorOpen} onClose={() => setConnectorOpen(false)} />
    </>
  );
}

function QuickAddMenu({ allowed }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const pick = (section) => { setOpen(false); triggerQuickAdd(section); };
  return (
    <div className="relative" ref={ref}>
      <button
        className="flex items-center gap-2 bg-maroon text-cream font-semibold rounded-lg px-3 sm:px-4 py-2.5 min-h-[44px] min-w-[44px] justify-center text-[15px] hover:-translate-y-0.5 transition-transform"
        data-testid="add-new-button"
        aria-haspopup="menu"
        aria-label="Add new"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Add new</span> <ChevronDown className={`w-4 h-4 transition-transform hidden sm:block ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-60 bg-cream border border-ink/20 rounded-xl shadow-2xl z-30 py-1.5" data-testid="add-new-menu" role="menu">
          {allowed.length === 0 ? (
            <div className="px-4 py-3 text-[14px] text-ink/55" data-testid="add-new-empty">You don't have permission to create anything yet.</div>
          ) : (
            allowed.map((o) => (
              <button
                key={o.section}
                data-testid={`add-new-${o.section}`}
                role="menuitem"
                onClick={() => pick(o.section)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-[15px] text-ink hover:bg-warm-cream/70"
              >
                <Icon name={o.icon} className="w-[18px] h-[18px] text-maroon" />
                {o.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const load = async () => { try { const r = await api.get("/notifications"); setData(r.data); } catch (e) {} };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  const go = async (n) => {
    if (!n.read) { try { await api.post(`/notifications/${n.notif_id}/read`); } catch (e) {} }
    if (n.entity_type === "member" && n.entity_id) sessionStorage.setItem("rca_open_member", n.entity_id);
    if (n.entity_type === "event" && n.entity_id) sessionStorage.setItem("rca_open_event", n.entity_id);
    setOpen(false);
    if (n.link_section) window.dispatchEvent(new CustomEvent("rca:navigate", { detail: { section: n.link_section } }));
    load();
  };
  const markAll = async () => { await api.post("/notifications/read-all"); load(); };
  return (
    <div className="relative">
      <button
        className="relative p-2.5 rounded-lg hover:bg-ink/5 min-h-[44px] min-w-[44px] flex items-center justify-center"
        aria-label="Notifications" data-testid="notifications-button"
        onClick={() => { setOpen((o) => !o); load(); }}
      >
        <Bell className="w-5 h-5 text-ink/70" />
        {data.unread > 0 && <span data-testid="notif-badge" className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-maroon text-cream text-[10px] font-bold flex items-center justify-center">{data.unread}</span>}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-cream border border-ink/20 rounded-xl shadow-2xl z-30" data-testid="notif-dropdown">
            <div className="flex items-center justify-between px-4 py-3 border-b border-ink/10 sticky top-0 bg-cream">
              <span className="font-zilla font-semibold">Notifications</span>
              {data.unread > 0 && <button data-testid="notif-mark-all" onClick={markAll} className="text-[12px] text-dark-olive font-semibold">Mark all read</button>}
            </div>
            {data.items.length === 0 && <div className="px-4 py-8 text-center text-ink/50 text-[14px]">Nothing yet.</div>}
            {data.items.map((n) => (
              <button key={n.notif_id} data-testid={`notif-item-${n.notif_id}`} onClick={() => go(n)}
                className={`w-full text-left px-4 py-3 border-b border-ink/8 last:border-0 hover:bg-warm-cream/60 ${n.read ? "opacity-60" : ""}`}>
                <div className="flex items-start gap-2">
                  {!n.read && <span className="w-2 h-2 rounded-full bg-maroon mt-1.5 shrink-0" />}
                  <div className={`flex-1 min-w-0 ${n.read ? "pl-4" : ""}`}>
                    <div className="text-[14px] font-semibold">{n.title}</div>
                    <div className="text-[13px] text-ink/65">{n.body}</div>
                    <div className="text-[11px] text-ink/40 mt-0.5 flex items-center gap-1">{new Date(n.created_at).toLocaleString()}{n.link_section && <span className="text-dark-olive font-semibold">· Open →</span>}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
